import { getLocale } from 'umi';
import moment from 'moment';
import { toDateTypeNor } from '@/pages/intelligence/utils/common';

const RegionsConfig: { [key: string]: { en: string; cn: string } } = {
  Worldwide: {
    en: 'Worldwide',
    cn: '全球',
  },
  Europe: {
    en: 'Europe',
    cn: '欧洲',
  },
  'Eastern Europe': {
    en: 'Eastern Europe',
    cn: '东欧',
  },
  'Western Europe': {
    en: 'Eastern Europe',
    cn: '东欧',
  },
  'North America': {
    en: 'North America',
    cn: '北美洲',
  },
  Japan: {
    en: 'Japan',
    cn: '日本',
  },
  Australia: {
    en: 'Australia',
    cn: '澳大利亚',
  },
  'South America': {
    en: 'South America',
    cn: '南美洲',
  },
  Asia: {
    en: 'Asia',
    cn: '亚洲',
  },
  'Southeast Asia': {
    en: 'Southeast Asia',
    cn: '东南亚',
  },
  Oceania: {
    en: 'Oceania',
    cn: '大洋洲',
  },
  China: {
    en: 'China',
    cn: '中国',
  },
  Korea: {
    en: 'Korea',
    cn: '韩国',
  },
  Taiwan: {
    en: 'Taiwan',
    cn: '台湾',
  },
  'Hong Kong': {
    en: 'Hong Kong',
    cn: '香港',
  },
  India: {
    en: 'India',
    cn: '印度',
  },
  'Middle East': {
    en: 'Middle East',
    cn: '中东',
  },
  Africa: {
    en: 'Africa',
    cn: '非洲',
  },
  Russia: {
    en: 'Russia',
    cn: '俄罗斯',
  },
};

export function getRegionNameByLocale(region: string): string {
  const locale = getLocale();
  const isCn = locale === 'zh-CN';
  if (typeof region !== 'string') return '';

  const currentRegion: { en: string; cn: string } = RegionsConfig[region];
  if (currentRegion) {
    return isCn ? currentRegion.cn : currentRegion.en;
  }
  return region;
}

const getReleaseRegionOrTypeName = (regionOrType: string, formatMessage: Function) => {
  const lowercaseRegionOrStatus = regionOrType?.toLowerCase() || '';

  // 发行样式
  // if (lowercaseRegionOrStatus === 'early access') return formatMessage({ id: 'dbi_early_access' });
  // if (lowercaseRegionOrStatus === 'full release') return formatMessage({ id: 'dbi_full_release' });
  // if (lowercaseRegionOrStatus === 'offline') return formatMessage({ id: 'dbi_off_line' });
  // if (lowercaseRegionOrStatus === 'beta') return formatMessage({ id: 'dbi_beta' });

  // 发行样式 map
  const textMap: { [key: string]: string } = {
    'early access': 'dbi_early_access',
    'full release': 'dbi_full_release',
    offline: 'dbi_off_line',
    beta: 'dbi_beta',
    alpha: 'dbi_alpha',
    released: 'dbi_released',
    cancelled: 'dbi_cancelled',
  };

  if (textMap[lowercaseRegionOrStatus]) {
    return formatMessage({ id: textMap[lowercaseRegionOrStatus] });
  }

  // 发行区域
  return getRegionNameByLocale(regionOrType);
};

export const releaseDateFormatter = (list: any, formatMessage: any) => {
  if (!Array.isArray(list)) return [];

  return list.map((item) => ({
    ...item,
    release_info: item.release_info?.map((releaseItem: any) => ({
      ...releaseItem,
      originStatus: releaseItem.status,
      originCountry: releaseItem.country,
      status: getReleaseRegionOrTypeName(releaseItem.status, formatMessage),
      country: getReleaseRegionOrTypeName(releaseItem.country, formatMessage),
    })),
  }));
};

export const parseReleaseDateByPlatformsJson = (json: string) => {
  if (!json) return [];
  try {
    return JSON.parse(json);
  } catch (e) {
    return [];
  }
};

export const getEarliestDateByRelease = (releseList: any[]) => {
  if (!Array.isArray(releseList)) return [];
  const releseListTemp = [...releseList];

  // 将release_date转换为日期对象以便比较
  releseListTemp?.forEach((item) => {
    item.release_date = new Date(item.release_date);
  });
  // 按release_date排序
  releseListTemp?.sort((a, b) => a.release_date - b.release_date);

  // 获取最早的时间
  const earliestTimeObj =
    Array.isArray(releseListTemp) && releseListTemp?.length > 0 ? releseListTemp[0] : null;
  return earliestTimeObj?.release_date || '';
};

// "PC", "PlayStation", "Xbox", "Nintendo

const getHasRegexDats = (str: string, list: any[]) => {
  const regex = new RegExp(list.join('|'));
  return regex.test(str);
};

const getHasPlatformRelease = (plat: string, list: any[]) => {
  const listCur = [...list];
  switch (plat) {
    case 'PC': {
      const platList = ['pc', 'mac', 'win', 'windows'];
      const listValue = listCur?.filter((item: any) =>
        getHasRegexDats(item.platform?.toLowerCase(), platList),
      );
      return listValue;
    }
    case 'PlayStation': {
      const platList = ['ps4', 'ps5', 'playstation'];
      const listValue = listCur?.filter((item: any) =>
        getHasRegexDats(item.platform?.toLowerCase(), platList),
      );
      return listValue;
    }
    case 'Xbox': {
      const platList = ['xbox', 'xone', 'xs'];
      const listValue = listCur?.filter((item: any) =>
        getHasRegexDats(item.platform?.toLowerCase(), platList),
      );
      return listValue;
    }
    case 'Nintendo': {
      const platList = ['nintendo', 'switch', 'wii u', 'ns'];
      const listValue = listCur?.filter((item: any) =>
        getHasRegexDats(item.platform?.toLowerCase(), platList),
      );
      return listValue;
    }
  }
};

export const getBaseReleaseTimeByDateTypeByPlatforms = (
  dateType: string,
  releaseList: any[],
  platforms: string[],
) => {
  const filterList = releaseList?.filter(
    (item: any) => !['iOS', 'ios', 'Android', 'android'].includes(item.platform),
  );
  const newRelaseList = filterList?.map((item: any) => {
    return {
      platform: item?.platform,
      release_date: getEarliestDateByRelease(item?.release_info),
    };
  });

  const platformList: any = [];
  platforms?.forEach((item) => {
    const resList: any = getHasPlatformRelease(item, newRelaseList);
    platformList.push(...resList);
  });

  const dateValue = moment(getEarliestDateByRelease(platformList)).format('YYYY-MM-DD');
  const formatDateValue = toDateTypeNor[dateType];
  switch (formatDateValue) {
    case 'week':
      return moment(dateValue).startOf('week').format('YYYY-MM-DD');
    case 'month':
      return `${moment(dateValue).format('YYYY-MM')}-01`;
    case 'day':
    default:
      return dateValue;
  }
};
